/**
 * SOLID Review
 * S — Strong. One concern: a state object that says when it changed — and only then.
 * O — Strong. Any state shape; nothing here names one.
 * L — N/A — no hierarchy.
 * I — Strong. Read, change, subscribe.
 * D — Strong. Imports nothing.
 */

/**
 * A VIEW MODEL'S OWN STATE (frontend-framework-oo P1, 2026-10-29, §28.293) — the plan's "small ObservableState collaborator":
 * composition, not a base class. A change that sets no field to a new value is no change — the same snapshot, no notification —
 * so a view model can hand `get()` to `useSyncExternalStore` as it is.
 */
export class ObservableState<TState extends object> {
  private current: TState;
  private readonly listeners = new Set<() => void>();

  constructor(initial: TState) {
    this.current = initial;
  }

  get(): TState {
    return this.current;
  }

  /** Lays `change` over the state: a new snapshot, and every listener told, only when a field takes a new value. */
  update(change: Partial<TState>): void {
    const keys = Object.keys(change) as Array<keyof TState>;
    if (keys.every((key) => Object.is(this.current[key], change[key]))) return;
    this.current = { ...this.current, ...change };
    for (const listener of [...this.listeners]) listener();
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }
}
